import _ from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { FacebookProvider, Login } from "react-facebook";
import { Button } from "reactstrap";
import {
  FACEBOOK_ADS_GET_LOADING,
  FACEBOOK_ADS_SYNC_LOADING,
} from "../../../../constants/loading.constant";
import { FACEBOOK_ACCOUNTS_SIDE_MODAL_RIGHT } from "../../../../constants/modal.constant";
import { APP_ID, SCOPE } from "../../../../cores/facebook/config";
import { useFacebookAdsCore } from "../../../../cores/facebookAds";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import {
  SocialProviderId,
  SocialProviderName,
} from "../../../../cores/socialPost/config";
import { useIntl } from "react-intl";
import { SocialProvider } from "../../../../cores/socialPost/interfaces/socialProvider";
import { daysRemainingFromToday } from "../../../utils/dateUtils";
import SocialCard from "../SocialCard";
import FacebookAdsAccounts from "./FacebookAdsAccounts";
import { FacebookAdsIcon } from "./FacebookAdsIcon";

interface FacebookAdsAuthButton {
  tokenDetail: { accessToken: string };
}

export default function FacebookAdsAuthButton() {
  const intl = useIntl();
  const { get, settings, unbind, getAccounts } = useFacebookAdsCore();
  const { company, getMeSocialSettings } = useMeCore();
  const { turnOnModal, isLoading } = useGlobalCore();

  useEffect(() => {
    get(company!.id);
  }, [company, get]);

  const socialProvider = useMemo(
    (): SocialProvider => ({
      id: SocialProviderId.FacebookAds,
      name: SocialProviderName.FacebookAds,
      icon: <FacebookAdsIcon size="5x" active={!_.isEmpty(settings)} />,
      displayName: settings?.name,
    }),
    [settings]
  );

  const handleOnError = useCallback(
    (error: string) => console.error(error),
    []
  );

  const handleOnCompleted = useCallback(
    async ({ tokenDetail: { accessToken } }: FacebookAdsAuthButton) => {
      await getAccounts(company!.id, accessToken);
      turnOnModal(
        FACEBOOK_ACCOUNTS_SIDE_MODAL_RIGHT,
        <FacebookAdsAccounts token={accessToken} />
      );
    },
    [company, getAccounts, turnOnModal]
  );

  const remainingBindDays = useMemo(
    () =>
      settings?.expiresIn ? daysRemainingFromToday(settings?.expiresIn) : null,
    [settings]
  );

  const handleUnbind = useCallback(() => {
    unbind(company!.id, () => {
      getMeSocialSettings(company?.id!);
    });
  }, [company, unbind, getMeSocialSettings]);

  const button = useMemo(() => {
    return _.isEmpty(settings) ? (
      <FacebookProvider version="v9.0" appId={APP_ID}>
        <Login
          scope={SCOPE}
          onCompleted={handleOnCompleted}
          onError={handleOnError}
        >
          {({ handleClick }: { handleClick: () => void }) => (
            <Button
              className="w-100 mx-auto"
              color="primary"
              onClick={handleClick}
            >
              {intl.formatMessage({ id: "app.home.syncUp" })}
            </Button>
          )}
        </Login>
      </FacebookProvider>
    ) : (
      <Button className="w-100 " color="primary" outline onClick={handleUnbind}>
        {intl.formatMessage({ id: "app.home.disconnect" })}
      </Button>
    );
  }, [handleOnCompleted, handleOnError, handleUnbind, settings, intl]);

  return button;
}
